import { Card } from "@blueprintjs/core/lib/esm/components/card/card";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import styled from "styled-components";
import * as Yup from "yup";

import { EPlatform, UsersApi } from "../../../../api";
import { PageContainer } from "../../../../components";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { EDrupalRoutes } from "../../../EDrupalRoutes";
import { ButtonsSection, FGCustomPanel, FGTextInput, showSuccess } from "nsitools-react";
import { Classes } from "@blueprintjs/core";

const StyledCard = styled(Card)`
  width: 40%;
  & .${Classes.FORM_GROUP} {
    margin: 0 0 15px !important;
  }
`;

export interface IDrupalChangePasswordPageProps {}

export const DrupalChangePasswordPage: React.FunctionComponent<IDrupalChangePasswordPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const userId = React.useMemo(() => +id, [id]);
  const api = useApiService(UsersApi);
  const { t } = useTl();
  const [loading, setLoading] = React.useState(false);

  const save = React.useCallback(
    async (values: any) => {
      setLoading(true);
      try {
        const res = await api.changePassword(values.password);
        setLoading(false);
        if (res !== null) {
          showSuccess(t(ETLCodes.PasswordChanged));
          history.push(EDrupalRoutes.login);
        }
        return res;
      } catch (e) {
        setLoading(false);
        throw e;
      }
    },
    [api, history, t, userId]
  );
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      password: Yup.string()
        .required(t(ETLCodes.Required))
        .test("is-equal", t(ETLCodes.PasswordMustMatch), async function (value) {
          return value === this.parent.passwordValidation;
        }),
      passwordValidation: Yup.string()
        .required(t(ETLCodes.Required))
        .test("is-equal", t(ETLCodes.PasswordMustMatch), async function (value) {
          return value === this.parent.password;
        }),
    });
  }, [t]);

  return (
    <PageContainer>
      <h1>{t(ETLCodes.PasswordChangeTitle)}</h1>
      <StyledCard elevation={2}>
        <DrupalFormGenerator
          initialValues={{ password: "", passwordValidation: "" }}
          onSubmit={save}
          editMode={true}
          validationSchema={FormSchema}
          labelAlignment="right"
          fill={true}
          inline={true}
          boldLabel={false}
          autoRequiredMark={true}
          showColons={false}
          hideButtons={true}
          enableDirtyCheck={false}
          large={true}
        >
          <FGTextInput name="password" label={t(ETLCodes.General_Password)} type="password" />
          <FGTextInput name="passwordValidation" label={t(ETLCodes.PasswordValidation)} type="password" />
          <FGCustomPanel>
            {({ formik }) => (
              <ButtonsSection
                buttons={[
                  {
                    outlined: true,
                    minimal: true,
                    position: "center",
                    text: t(ETLCodes.Validate),
                    intent: "primary",
                    type: "submit",
                    large: true,
                    loading: loading,
                    disabled: !(formik.touched && formik.isValid),
                  },
                ]}
              />
            )}
          </FGCustomPanel>
        </DrupalFormGenerator>
      </StyledCard>
    </PageContainer>
  );
};
